import type { DraftQuotation } from './api.ts'

/**
 * Keeping a half-built simulation across a reload.
 *
 * Editing a quotation into the divergence you want takes a few minutes, and
 * losing it to a refresh or a closed tab is the kind of small cruelty that
 * stops people using an internal tool at all.
 *
 * Keyed by the upload id, which the server already assigned — inventing a
 * second identifier would mean two ids for one thing and a mapping between
 * them that can go stale. The id is in the URL, so returning to the draft is
 * just going back to the page.
 *
 * Local to one browser, deliberately. A simulation is somebody's scratch work,
 * not a shared artefact, and the server already holds the upload and the
 * reading it derived — this stores only the edits made on top.
 */

const PREFIX = 'rp.simulation.'

export interface StoredDraft {
  readonly uploadId: string
  readonly quotation: DraftQuotation
  /** Which step was on screen, so a return lands where it left. */
  readonly step: number
  readonly updatedAt: string
}

/**
 * Every read and write is guarded.
 *
 * `localStorage` throws outright in a private window and in browsers set to
 * block site data — not returning null, throwing — so an unguarded read takes
 * the whole page down rather than losing a draft.
 */
export function loadDraft(uploadId: string): StoredDraft | undefined {
  try {
    const raw = localStorage.getItem(PREFIX + uploadId)
    if (raw === null) return undefined
    const parsed = JSON.parse(raw) as StoredDraft
    return parsed.quotation === undefined ? undefined : parsed
  } catch {
    return undefined
  }
}

export function saveDraft(draft: Omit<StoredDraft, 'updatedAt'>): void {
  try {
    localStorage.setItem(
      PREFIX + draft.uploadId,
      JSON.stringify({ ...draft, updatedAt: new Date().toISOString() }),
    )
  } catch {
    // A full or unavailable store loses the resume, not the session.
  }
}

export function forgetDraft(uploadId: string): void {
  try {
    localStorage.removeItem(PREFIX + uploadId)
  } catch {
    // Nothing to do: the draft simply lingers until the browser clears it.
  }
}

/** Every draft this browser is holding, newest first. */
export function listDrafts(): StoredDraft[] {
  const drafts: StoredDraft[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key === null || !key.startsWith(PREFIX)) continue
      const draft = loadDraft(key.slice(PREFIX.length))
      if (draft !== undefined) drafts.push(draft)
    }
  } catch {
    return []
  }
  return drafts.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
}
